/**
 * Förinställda tariffer per nätbolag.
 *
 * Källor: nätbolagens egna prislistor. Datan är manuellt kuraterad och
 * måste uppdateras när bolagen ändrar priser (typiskt 1 januari, ibland
 * 1 juli). Varje tariff har validFrom/validTo så historiska perioder
 * räknas mot rätt prisbild.
 *
 * Priserna är alltid i kr EXKL moms. Officiella prislistor är ofta inkl
 * moms — dela med 1.25 vid inmatning.
 *
 * Stödda bolag:
 * - Vattenfall Eldistribution (~900 000 kunder, störst)
 * - Ellevio (~1 000 000 kunder, säkringsmodell från juni 2026)
 * - E.on Energidistribution Syd (regional)
 * - Generisk fallback
 */

import type { GridFeeConfig } from "../sim/types";

export interface GridFeePreset {
  id: string;
  operator: string;
  fuseSize: number;
  label?: string;
  description: string;
  geographicHints?: string[];
  tariff: GridFeeConfig;
}

// ============================================================
// Vattenfall Eldistribution — säkringsabonnemang 2026 enkeltariff
// Källa: https://www.vattenfalleldistribution.se/abonnemang-och-avgifter/
// avtal-och-avgifter/elnatsavgifter/sakringsabonnemang-16-63a/
// ============================================================

export const VATTENFALL_16A_2026: GridFeePreset = {
  id: "vattenfall-16a-2026",
  operator: "Vattenfall Eldistribution",
  fuseSize: 16,
  description: "Säkringsabonnemang 16A, enkeltariff.",
  geographicHints: ["Stockholm", "Uppsala", "Gävle", "Norrbotten", "Västerbotten"],
  tariff: {
    operator: "Vattenfall Eldistribution",
    area: "Sverige",
    fuseSize: 16,
    validFrom: "2026-01-01",
    validTo: null as unknown as string,
    sourceUrl:
      "https://www.vattenfalleldistribution.se/abonnemang-och-avgifter/avtal-och-avgifter/elnatsavgifter/sakringsabonnemang-16-63a/",
    // 5775 kr/år inkl moms → 4620/12 = 385 kr/mån exkl moms
    fixedMonthlySek: 385,
    // 44.5 öre/kWh inkl moms → 35.6 öre/kWh exkl moms
    transmissionPerKWh: 0.356,
    feedInCompensationPerKWh: 0.05,
  },
};

export const VATTENFALL_20A_2026: GridFeePreset = {
  id: "vattenfall-20a-2026",
  operator: "Vattenfall Eldistribution",
  fuseSize: 20,
  description: "Säkringsabonnemang 20A, enkeltariff.",
  geographicHints: ["Stockholm", "Uppsala", "Gävle"],
  tariff: {
    operator: "Vattenfall Eldistribution",
    area: "Sverige",
    fuseSize: 20,
    validFrom: "2026-01-01",
    validTo: null as unknown as string,
    sourceUrl:
      "https://www.vattenfalleldistribution.se/abonnemang-och-avgifter/avtal-och-avgifter/elnatsavgifter/sakringsabonnemang-16-63a/",
    // 8085 kr/år inkl moms → 6468/12 = 539 kr/mån exkl moms
    fixedMonthlySek: 539,
    transmissionPerKWh: 0.356,
    feedInCompensationPerKWh: 0.05,
  },
};

export const VATTENFALL_25A_2026: GridFeePreset = {
  id: "vattenfall-25a-2026",
  operator: "Vattenfall Eldistribution",
  fuseSize: 25,
  description: "Säkringsabonnemang 25A, enkeltariff.",
  geographicHints: ["Stockholm", "Uppsala", "Gävle"],
  tariff: {
    operator: "Vattenfall Eldistribution",
    area: "Sverige",
    fuseSize: 25,
    validFrom: "2026-01-01",
    validTo: null as unknown as string,
    sourceUrl:
      "https://www.vattenfalleldistribution.se/abonnemang-och-avgifter/avtal-och-avgifter/elnatsavgifter/sakringsabonnemang-16-63a/",
    // 10125 kr/år inkl moms → 8100/12 = 675 kr/mån exkl moms
    fixedMonthlySek: 675,
    transmissionPerKWh: 0.356,
    feedInCompensationPerKWh: 0.05,
  },
};

// ============================================================
// Ellevio — säkringsabonnemang från 1 juni 2026
// Källa: https://www.ellevio.se/abonnemang/elnatspriser/hus/
// ============================================================

export const ELLEVIO_16A_2026: GridFeePreset = {
  id: "ellevio-16a-2026",
  operator: "Ellevio",
  fuseSize: 16,
  description: "Säkringsabonnemang 16A (modell från 1 juni 2026).",
  geographicHints: ["Stockholm", "Värmland", "Halland", "Bohuslän", "Västra Götaland"],
  tariff: {
    operator: "Ellevio",
    area: "Sverige",
    fuseSize: 16,
    validFrom: "2026-06-01",
    validTo: null as unknown as string,
    sourceUrl: "https://www.ellevio.se/abonnemang/elnatspriser/hus/",
    fixedMonthlySek: 384,
    transmissionPerKWh: 0.208,
    feedInCompensationPerKWh: 0.05,
  },
};

export const ELLEVIO_20A_2026: GridFeePreset = {
  id: "ellevio-20a-2026",
  operator: "Ellevio",
  fuseSize: 20,
  description: "Säkringsabonnemang 20A (modell från 1 juni 2026).",
  geographicHints: ["Stockholm", "Värmland", "Halland", "Västra Götaland"],
  tariff: {
    operator: "Ellevio",
    area: "Sverige",
    fuseSize: 20,
    validFrom: "2026-06-01",
    validTo: null as unknown as string,
    sourceUrl: "https://www.ellevio.se/abonnemang/elnatspriser/hus/",
    fixedMonthlySek: 472,
    transmissionPerKWh: 0.208,
    feedInCompensationPerKWh: 0.05,
  },
};

export const ELLEVIO_25A_2026: GridFeePreset = {
  id: "ellevio-25a-2026",
  operator: "Ellevio",
  fuseSize: 25,
  description: "Säkringsabonnemang 25A (modell från 1 juni 2026).",
  geographicHints: ["Stockholm", "Värmland", "Halland", "Västra Götaland"],
  tariff: {
    operator: "Ellevio",
    area: "Sverige",
    fuseSize: 25,
    validFrom: "2026-06-01",
    validTo: null as unknown as string,
    sourceUrl: "https://www.ellevio.se/abonnemang/elnatspriser/hus/",
    fixedMonthlySek: 576,
    transmissionPerKWh: 0.208,
    feedInCompensationPerKWh: 0.05,
  },
};

// ============================================================
// E.on Energidistribution Syd — säkringsabonnemang 2026
// E.on har INTE infört effektavgift än. Planerad till sept 2026 är pausad
// efter regeringens stopp av effekttariff-kravet. Tills vidare gäller
// säkringsabonnemang med fast avgift + transmission per kWh.
// Notera: priserna nedan är approximerade och behöver verifieras mot
// E.on:s prislista för ditt specifika område. Tipsa oss gärna om
// exakta priser via mailto-länken.
// Källa: https://www.eon.se/el/elnat/priser
// ============================================================

export const EON_SYD_16A_2026: GridFeePreset = {
  id: "eon-syd-16a-2026",
  operator: "E.on Energidistribution Syd",
  fuseSize: 16,
  description: "Säkringsabonnemang 16A.",
  geographicHints: ["Skåne", "Blekinge", "Småland", "Halland", "Kalmar"],
  tariff: {
    operator: "E.on Energidistribution Syd",
    area: "Syd",
    fuseSize: 16,
    validFrom: "2026-01-01",
    validTo: null as unknown as string,
    sourceUrl: "https://www.eon.se/el/elnat/priser",
    // Approximerade värden — verifiera mot din egen faktura
    fixedMonthlySek: 240,
    transmissionPerKWh: 0.30,
    feedInCompensationPerKWh: 0.05,
  },
};

export const EON_SYD_20A_2026: GridFeePreset = {
  id: "eon-syd-20a-2026",
  operator: "E.on Energidistribution Syd",
  fuseSize: 20,
  description: "Säkringsabonnemang 20A.",
  geographicHints: ["Skåne", "Blekinge", "Småland", "Halland", "Kalmar"],
  tariff: {
    operator: "E.on Energidistribution Syd",
    area: "Syd",
    fuseSize: 20,
    validFrom: "2026-01-01",
    validTo: null as unknown as string,
    sourceUrl: "https://www.eon.se/el/elnat/priser",
    fixedMonthlySek: 340,
    transmissionPerKWh: 0.30,
    feedInCompensationPerKWh: 0.05,
  },
};

export const EON_SYD_25A_2026: GridFeePreset = {
  id: "eon-syd-25a-2026",
  operator: "E.on Energidistribution Syd",
  fuseSize: 25,
  description: "Säkringsabonnemang 25A.",
  geographicHints: ["Skåne", "Blekinge", "Småland", "Halland", "Kalmar"],
  tariff: {
    operator: "E.on Energidistribution Syd",
    area: "Syd",
    fuseSize: 25,
    validFrom: "2026-01-01",
    validTo: null as unknown as string,
    sourceUrl: "https://www.eon.se/el/elnat/priser",
    fixedMonthlySek: 440,
    transmissionPerKWh: 0.30,
    feedInCompensationPerKWh: 0.05,
  },
};

// ============================================================
// Generisk fallback
// ============================================================

export const GENERIC_SE: GridFeePreset = {
  id: "generic-se",
  operator: "Annat / okänt nätbolag",
  fuseSize: 16,
  description: "Ungefärligt riksgenomsnitt. Använd om ditt nätbolag inte finns i listan.",
  tariff: {
    operator: "Generisk",
    area: "Sverige",
    fuseSize: 16,
    fixedMonthlySek: 400,
    transmissionPerKWh: 0.3,
    feedInCompensationPerKWh: 0.05,
  },
};

export const GRID_FEE_PRESETS: GridFeePreset[] = [
  VATTENFALL_16A_2026,
  VATTENFALL_20A_2026,
  VATTENFALL_25A_2026,
  ELLEVIO_16A_2026,
  ELLEVIO_20A_2026,
  ELLEVIO_25A_2026,
  EON_SYD_16A_2026,
  EON_SYD_20A_2026,
  EON_SYD_25A_2026,
  GENERIC_SE,
];

export const DEFAULT_PRESET_ID = VATTENFALL_20A_2026.id;

export function groupPresetsByOperator(): { operator: string; presets: GridFeePreset[] }[] {
  const groups = new Map<string, GridFeePreset[]>();
  for (const preset of GRID_FEE_PRESETS) {
    const existing = groups.get(preset.operator);
    if (existing) existing.push(preset);
    else groups.set(preset.operator, [preset]);
  }
  return Array.from(groups, ([operator, presets]) => ({ operator, presets }));
}

/**
 * Försöker matcha en Tibber gridArea-sträng mot en av våra presets.
 * Returnerar null om ingen träff finns.
 */
export function matchPresetByGridArea(
  gridArea: string | null,
  fuseSize: number = 20,
): GridFeePreset | null {
  if (!gridArea) return null;
  const normalized = gridArea.toLowerCase();

  if (normalized.includes("vattenfall")) {
    return (
      GRID_FEE_PRESETS.find(
        (p) => p.operator === "Vattenfall Eldistribution" && p.fuseSize === fuseSize,
      ) ?? VATTENFALL_20A_2026
    );
  }
  if (normalized.includes("ellevio")) {
    return (
      GRID_FEE_PRESETS.find(
        (p) => p.operator === "Ellevio" && p.fuseSize === fuseSize,
      ) ?? ELLEVIO_20A_2026
    );
  }
  if (normalized.includes("eon") || normalized.includes("e.on")) {
    return EON_SYD_16A_2026;
  }

  return null;
}
